import axios from 'axios';
import { translations } from '../translations';

const getCurrentLanguage = () => {
  return localStorage.getItem('language') || 'tr';
};

const t = (key) => {
  const language = getCurrentLanguage();
  return translations[language]?.[key] || translations['tr'][key] || key;
};

const clearSession = () => {
  // console.log('Clearing session data...');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('sessionUserId');
  localStorage.removeItem('temp_token');
  localStorage.removeItem('authToken');
};

const redirectToLogin = (message) => {
  const defaultMessage = t('sessionExpired');
  const finalMessage = message || defaultMessage;
  
  // console.log('Redirecting to login:', finalMessage);
  
  if (window.showAlert) {
    window.showAlert({
      title: t('sessionExpired'),
      text: finalMessage,
      variant: 'warning'
    });
  }
  
  setTimeout(() => {
    window.location.href = '/login';
  }, 1500);
};

const handleSessionError = (error, context = '') => {
  console.error(`Session error in ${context}:`, error);
  
  if (error.response) {
    const status = error.response.status;
    
    if (error.response.data?.Result?.ErrorDescription?.includes('token is expired')) {
      // console.log('JWT token expired detected in service');
      clearSession();
      redirectToLogin(t('sessionExpired'));
      return true;
    }
    
    if (status === 401) {
      // console.log('401 Unauthorized - session expired');
      clearSession();
      redirectToLogin(t('unauthorizedError'));
      return true;
    }
    
    if (status === 403) {
      // console.log('403 Forbidden - session invalid');
      clearSession();
      redirectToLogin(t('accessDeniedError'));
      return true;
    }
    
    if (error.response.data?.Result) {
      const errorCode = error.response.data.Result.ErrorCode;
      const errorDesc = error.response.data.Result.ErrorDescription;
      
      const sessionErrorCodes = [1001, 1002, 1003, 9999];
      
      if (sessionErrorCodes.includes(errorCode)) {
        // console.log(`Session error code detected: ${errorCode}`);
        clearSession();
        redirectToLogin(errorDesc || t('sessionError'));
        return true;
      }
    }
  }
  
  // Network Error handling'i kaldırıldı - artık session error olarak değerlendirilmiyor
  
  return false;
};

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    return 'Bearer ' + token.replace(/"/g, '');
  }
  const user = JSON.parse(localStorage.getItem('user'));
  const userToken = user?.token;
  
  if (userToken) {
    return 'Bearer ' + userToken.replace(/"/g, '');
  }
  
  return ''; 
};

const getTempToken = () => {
  const tempToken = localStorage.getItem('temp_token');

  if (tempToken) {
    return 'Bearer ' + tempToken.replace(/"/g, '');
  }
  return ''; 
};

export const axiosGet = async (options) => {
  try {
    let fullApiPath = `${process.env.REACT_APP_API_URL}${options.endpoint}`;
    const token = await getToken();
    
    // console.log('axiosGet - Endpoint:', options.endpoint);
    // console.log('axiosGet - Token:', token ? 'Present' : 'Missing');
    
    let res = await axios.get(fullApiPath, {
      params: options.body,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: token }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezoneOffset: new Date().getTimezoneOffset(),
      },
    });
    
    return res;
  } catch (error) {
    console.error('axiosGet error:', error);
    
    const isSessionError = handleSessionError(error, 'axiosGet');
    
    if (isSessionError) {
      throw {
        ...error,
        isSessionExpired: true,
        message: t('sessionExpired')
      };
    }
    throw error;
  }
};

const convertFormDataToJson = (formData) => {
  if (formData instanceof FormData) {
    const jsonData = {};
    for (let [key, value] of formData.entries()) {
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
      jsonData[formattedKey] = value;
    }
    return jsonData;
  }
  return formData;
};

export const axiosPost = async (options) => {
  try {
    let fullApiPath = `${process.env.REACT_APP_API_URL}${options.endpoint}`;
    const token = options.endpoint.startsWith('/verify_2fa/') 
      ? await getTempToken() : await getToken();
    
    const finalBody = convertFormDataToJson(options.body);
    
    // console.log('axiosPost - Full API Path:', fullApiPath);
    // console.log('axiosPost - Token:', token ? 'Present' : 'Missing');
    // console.log('axiosPost - Final Body:', finalBody);

    let res = await axios.post(fullApiPath, finalBody, {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: token }),
      },
    });

    return res;
  } catch (error) {
    console.error('axiosPost error:', error);
    
    const isSessionError = handleSessionError(error, 'axiosPost');
    
    if (isSessionError) {
      throw {
        ...error,
        isSessionExpired: true,
        message: t('sessionExpired')
      };
    }
    
    throw error;
  }
};

export const axiosDelete = async (options) => {
  try {
    let fullApiPath = `${process.env.REACT_APP_API_URL}${options.endpoint}`;
    const token = await getToken();

    // console.log('axiosDelete - Endpoint:', options.endpoint);
    // console.log('axiosDelete - Token:', token ? 'Present' : 'Missing');

    let res = await axios.delete(fullApiPath, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      data: options.body,
    });

    return res;
  } catch (error) {
    console.error('axiosDelete error:', error);
    
    const isSessionError = handleSessionError(error, 'axiosDelete');
    
    if (isSessionError) {
      throw {
        ...error,
        isSessionExpired: true,
        message: t('sessionExpired')
      };
    }
    
    throw error;
  }
};

export const axiosPut = async (options) => {
  try {
    let fullApiPath = `${process.env.REACT_APP_API_URL}${options.endpoint}`;
    const token = await getToken();

    // console.log('axiosPut - Endpoint:', options.endpoint);
    // console.log('axiosPut - Token:', token ? 'Present' : 'Missing');

    let res = await axios.put(fullApiPath, options.body, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
    });
    
    return res;
  } catch (error) {
    console.error('axiosPut error:', error);
    
    const isSessionError = handleSessionError(error, 'axiosPut');
    
    if (isSessionError) {
      throw {
        ...error,
        isSessionExpired: true,
        message: t('sessionExpired')
      };
    }
    
    throw error;
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Global axios interceptor error:', error);
    
    return Promise.reject(error);
  }
);